import { getRuntimeConfig } from "@/shared/config"
import { getTelegramUsername } from "@/shared/api/auth"

import type { AnalyticsEventName, AnalyticsEventParams } from "./events"

let analyticsEnabled = false
let identifiedUserId: string | null = null
let identifiedTelegramUsername: string | null = null

export function setAnalyticsEnabled(enabled: boolean): void {
  analyticsEnabled = enabled
}

function isAnalyticsActive(): boolean {
  if (!analyticsEnabled) return false
  try {
    return getRuntimeConfig().env === "prod"
  } catch {
    return false
  }
}

function pushToDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === "undefined") return
  const w = window as Window & { dataLayer?: Record<string, unknown>[] }
  w.dataLayer = w.dataLayer || []
  w.dataLayer.push(payload)
}

function syncTelegramUsernameFromClient(): void {
  const username = getTelegramUsername()
  if (username) identifiedTelegramUsername = username
}

function appendIdentityFields(payload: Record<string, unknown>): void {
  if (identifiedUserId) payload.user_id = identifiedUserId
  if (identifiedTelegramUsername) {
    payload.telegram_username = identifiedTelegramUsername
  }
}

/** @username (without `@`) from initData (available before JWT). */
export function identifyAnalyticsTelegramUser(): void {
  syncTelegramUsernameFromClient()
  if (!identifiedTelegramUsername) return
  if (!isAnalyticsActive()) return
  const payload: Record<string, unknown> = {}
  appendIdentityFields(payload)
  pushToDataLayer(payload)
}

/** Backend account id (JWT `sub`); call after auth. */
export function identifyAnalyticsUser(userId: string | null | undefined): void {
  syncTelegramUsernameFromClient()
  if (userId) {
    identifiedUserId = userId
  }
  if (!isAnalyticsActive()) return
  const payload: Record<string, unknown> = {}
  appendIdentityFields(payload)
  if (Object.keys(payload).length > 0) {
    pushToDataLayer(payload)
  }
}

export function clearAnalyticsUser(): void {
  identifiedUserId = null
  identifiedTelegramUsername = null
}

export function trackAnalyticsEvent<E extends AnalyticsEventName>(
  event: E,
  params: AnalyticsEventParams[E]
): void {
  if (!isAnalyticsActive()) return

  const payload: Record<string, unknown> = {
    event,
    ...params,
  }
  syncTelegramUsernameFromClient()
  appendIdentityFields(payload)
  pushToDataLayer(payload)
}
